import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Video, FileText, ArrowLeft, MessageSquare } from 'lucide-react';
import { topics } from '@/data/videoTopics';
import SEO from '@/components/SEO';

const CategoryOptionsPage = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const topicData = topics.find(topic => topic.id === categoryId);

  if (!topicData) {
    return (
      <div className="min-h-screen">
        <Header />
        <main className="pt-32 pb-20 bg-background">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h1 className="font-rockwell text-4xl font-semibold text-primary mb-6">
              Category Not Found
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              The requested category could not be found.
            </p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen relative">
      <Header />
      <SEO
        title={`${topicData.title} Resources | Freeman & Crown Law`}
        description={`Access videos and documents for ${topicData.title}. ${topicData.description}`}
        image="/lovable-uploads/8c630ffa-2bac-4996-be73-277885cc03f3.png"
        url={window.location.origin + `/category/${categoryId}`}
        type="website"
      />
      
      {/* Loading state */}
      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-md">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      )}
      
      {/* Auth overlay when user is not logged in */}
      {!user && !loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/60 backdrop-blur-sm p-4">
          <Card className="w-full max-w-lg border-2 border-primary/30 shadow-2xl bg-background/95">
            <CardContent className="pt-6 pb-6 sm:pt-8 sm:pb-8 px-6 text-center space-y-5">
              <div className="space-y-2.5">
                <h2 className="text-2xl sm:text-3xl font-rockwell font-bold text-primary">
                  Private Members Only
                </h2>
                <p className="text-base sm:text-lg text-muted-foreground">
                  You need to be logged in to access videos, documents, and forums in this category
                </p>
                <p className="text-sm text-muted-foreground/80">
                  Don't have an account? Sign up for free!
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Button 
                  onClick={() => navigate('/auth')}
                  className="w-full sm:flex-1"
                  size="default"
                >
                  Login
                </Button>
                <Button 
                  onClick={() => navigate('/auth?tab=signup')}
                  variant="outline"
                  className="w-full sm:flex-1"
                  size="default"
                >
                  Join Free
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      <main className="pt-32 pb-20 bg-background">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="font-rockwell text-4xl md:text-5xl font-semibold text-primary mb-6">
              {topicData.title}
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              {topicData.description}
            </p>
          </div>

          <div className="max-w-lg mx-auto">
            <Card className="bg-gradient-to-br from-primary/5 to-gold/10 border border-gold/20 rounded-xl">
              <CardHeader className="text-center pb-4">
                <p className="text-muted-foreground">
                  Access videos and documents related to {topicData.title.toLowerCase()}.
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <Link to={`/category/${categoryId}/videos`} className="block">
                  <Button className="w-full bg-gold-light hover:bg-gold-light/90 text-gold-foreground font-semibold border border-primary h-12">
                    <Video className="mr-2 h-5 w-5" />
                    View Videos ({topicData.videoCount} available)
                  </Button>
                </Link>
                <Link to={`/category/${categoryId}/resources`} className="block">
                  <Button className="w-full bg-gold-light hover:bg-gold-light/90 text-gold-foreground font-semibold border border-primary h-12">
                    <FileText className="mr-2 h-5 w-5" />
                    View Documents
                  </Button>
                </Link>
                <Link to={`/category/${categoryId}/forum`} className="block">
                  <Button className="w-full bg-gold-light hover:bg-gold-light/90 text-gold-foreground font-semibold border border-primary h-12">
                    <MessageSquare className="mr-2 h-5 w-5" />
                    Community Forum
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CategoryOptionsPage;